import React, { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { extent, merge } from 'd3-array';
import { select } from 'd3-selection';
import { timeDay } from 'd3-time';
import { Link, useLocation } from 'react-router-dom';

import DayFilterContext from '../DayFilterContext';
import DayPicker from './DayPicker';
import { getDaysScrollKeyHandler } from './Days';
import GraphicalDiaryView from './GraphicalDiaryView';
import MainView from './MainView';
import PresentationContext from '../PresentationContext';
import TabularDiaryView from './TabularDiaryView';
import TargetContext from './TargetContext';
import { internalUnits, consts } from '../utils/consts';
import { parseDate, urlDateFormat } from '../utils/formats';
import './Dashboard.css';

const defaultTicks = consts[internalUnits].ticks;

function DailyView(props) {
  const {
    day,
    days,
    interactive = true,
    basalData,
    bolusData,
    // eventData,
    exerciseData,
    longActingData,
    // notesData,
    nutritionData,
    // photosData,
    profilesData,
    sgvData,
    sleepData,
    smbgData,
    stepsData,
    t,
    timeScale,
  } = props;

  const {
    formatDate,
    weekInterval,
  } = useContext(PresentationContext);

  const { invalidRange, setDays: setContextDays } = useContext(DayFilterContext);

  const setDays = useMemo(() => function setDay(from, to) {
    setContextDays(timeDay.range(from, timeDay.offset(to, 1)).map(urlDateFormat));
  }, [setContextDays]);

  const location = useLocation();
  const { hash } = location;
  const sgvValues = useMemo(() => merge(Object.values(sgvData)), [sgvData]);
  const smbgValues = useMemo(() => merge(Object.values(smbgData)), [smbgData]);

  const [showTabularView, setTabularView] = useState((hash === '#tabular')
  || (!sgvValues.length && !!smbgValues.length));

  useEffect(() => {
    if (!(hash === '#graphical') && !showTabularView && !sgvValues.length && !!smbgValues.length) {
      // "downgrade" to tabular view automatically
      // Should we also "upgrade" respectively?
      // console.log('Effect downgrading!');
      setTabularView('downgraded');
    } else if ((showTabularView === 'downgraded') && sgvValues.length && !(hash === '#tabular')) {
      // console.log('Effect upgrading!');
      setTabularView(false);
    }
  }, [hash, sgvValues, showTabularView, smbgValues]);

  const inputHandler = useMemo(() => getDaysScrollKeyHandler({ days, setDays, weekInterval }),
    [days, setDays, weekInterval]);

  useEffect(() => {
    if (interactive) {
      select(document).on('keydown.scrollDays', inputHandler);
    }
    return () => {
      select(document).on('keydown.scrollDays', null);
    };
  }, [inputHandler, interactive]);

  const dayNavi = useMemo(() => (
    <Fragment>
      <button
        className="inline minimal"
        onClick={(event) => {
          inputHandler({
            synthEvent: {
              ...event,
              key: 'ArrowLeft',
            }
          });
        }}
      >
        ‹
      </button>
      <DayPicker
        formatDate={formatDate}
        day={days[0]}
        error={invalidRange}
        setDate={(date) => {
          const validatedDate = new Date(date);
          if (!isNaN(validatedDate)) {
            const startDate = parseDate(date);
            setDays(startDate, timeDay.offset(startDate, days.length - 1));
          }
        }}
      />
      {t('common.rangeSeparator')}
      <DayPicker
        formatDate={formatDate}
        day={days[days.length - 1]}
        error={invalidRange}
        setDate={(date) => {
          const validatedDate = new Date(date);
          if (!isNaN(validatedDate)) {
            const startDate = parseDate(days[0]);
            const endDate = parseDate(date);
            setDays(startDate, endDate);
          }
        }}
      />
      <button
        className="inline minimal"
        onClick={(event) => {
          inputHandler({
            synthEvent: {
              ...event,
              key: 'ArrowRight',
            }
          });
        }}
      >
        ›
      </button>
    </Fragment>
  ), [formatDate, days, inputHandler, invalidRange, setDays, t]);

  const basalExtent = useMemo(() => extent(merge(Object.values(basalData)), n => n.rate), [basalData]);
  const bolusExtent = useMemo(() => extent(merge(Object.values(bolusData)), n => n.amount), [bolusData]);
  const carbExtent = useMemo(() => extent(merge(Object.values(nutritionData)), n => n.carbohydrate), [nutritionData]);
  const longActingExtent = useMemo(() => extent(merge(Object.values(longActingData)), n => n.amount), [longActingData]);
  const maxCarbs = useMemo(() => Math.max(...Object.values(nutritionData).map(arr => arr.reduce((t, n) => t + (n.carbohydrate || 0), 0))) || 350, [nutritionData]);
  const maxSteps = useMemo(() => Math.max(...Object.values(stepsData).map(arr => arr.reduce((t, s) => t + s.steps, 0))) || 1, [stepsData]);
  const maxExercise = useMemo(() => Math.max(...Object.values(exerciseData).map(arr => arr.reduce((t, e) => t + e.duration, 0))) || 1, [exerciseData]);
  const maxSleep = useMemo(() => Math.max(...Object.values(sleepData).map(arr => arr.reduce((t, s) => t + s.duration, 0))) || 1, [sleepData]);

  const times = days.map(parseDate);

  const { targets } = useContext(TargetContext);
        
  const ticks = useMemo(() => ({
    ...defaultTicks,
    high: targets.top,
    low: targets.bottom,
  }), [targets]);

  const viewProps = {
    day,
    days,
    interactive,
    basalData,
    basalExtent,
    bolusData,
    bolusExtent,
    carbExtent,
    // eventData,
    exerciseData,
    // notesData,
    longActingData,
    longActingExtent,
    maxCarbs,
    maxExercise,
    maxSleep,
    maxSteps,
    nutritionData,
    profilesData,
    // photosData,
    sgvData,
    sleepData,
    smbgData,
    stepsData,
    t,
    ticks,
    timeScale,
  };

  const description = (
    <Fragment>
      {t('diary.description', { view: showTabularView ? 'tabularView' : 'graphicalView' })}
      {' '}
      {t('diary.altDescription')}
      {' '}
      <Link
        to={showTabularView ? '#graphical' : '#tabular'}
        onClick={() => setTabularView(!showTabularView)}
      >
        {t(`diary.${!showTabularView ? 'tabularView' : 'graphicalView'}`)}
      </Link>.
    </Fragment>
  )

  return (
    <MainView
      description={description}
      heading={<Fragment>
        {t('diary.heading')} {dayNavi}
      </Fragment>}
      interactive={interactive}
      key="mainView"
      times={times}
      title={`Sensotrend Diary View: `}
    >
      { showTabularView
      ? (
        <TabularDiaryView { ...viewProps} />
      )
      : (
        <GraphicalDiaryView {...viewProps} />
      )
      }
    </MainView>
  );
};

export default DailyView;
